import { useRef, useState, useCallback, useEffect } from 'react'

const INWORLD_KEY = (import.meta.env.VITE_INWORLD_API_KEY as string) || ''
const INWORLD_ENDPOINT = 'https://api.inworld.ai/tts/v1/voice:stream'
const DEFAULT_VOICE = 'Carter'
const MODEL_ID = 'inworld-tts-1.5-max'
const SAMPLE_RATE = 22050

export function useInworldTTS(enabled: boolean) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const queueRef = useRef<Blob[]>([])
  const isPlayingRef = useRef(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const abortRef = useRef<AbortController | null>(null)
  const currentUrlRef = useRef<string | null>(null)

  // Get or create the <audio> element
  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const el = document.getElementById('tts-audio') as HTMLAudioElement | null
      if (el) {
        audioRef.current = el
      } else {
        const a = document.createElement('audio')
        a.id = 'tts-audio'
        a.autoplay = true
        document.body.appendChild(a)
        audioRef.current = a
      }
    }
    return audioRef.current
  }, [])

  const playNext = useCallback(() => {
    if (queueRef.current.length === 0) {
      isPlayingRef.current = false
      setIsPlaying(false)
      return
    }
    isPlayingRef.current = true
    setIsPlaying(true)

    const blob = queueRef.current.shift()!
    const url = URL.createObjectURL(blob)

    // Revoke previous URL
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current)
    }
    currentUrlRef.current = url

    const audio = getAudio()
    audio.src = url
    audio.onended = () => playNext()
    audio.onerror = () => {
      console.error('[TTS] audio play error')
      playNext()
    }
    audio.play().catch((e) => {
      console.error('[TTS] play() rejected:', e)
      // Autoplay blocked — try again on next tick
      setTimeout(() => audio.play().catch(() => playNext()), 100)
    })
  }, [getAudio])

  const speak = useCallback(async (text: string, voiceId?: string) => {
    if (!enabled || !INWORLD_KEY || !text.trim()) return

    // Sanitize voiceId — 'default' is not a valid Inworld voice
    const resolvedVoice = (!voiceId || voiceId === 'default') ? DEFAULT_VOICE : voiceId

    if (abortRef.current) abortRef.current.abort()
    abortRef.current = new AbortController()

    try {
      const res = await fetch(INWORLD_ENDPOINT, {
        method: 'POST',
        headers: {
          'Authorization': `Basic ${INWORLD_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          voiceId: resolvedVoice,
          modelId: MODEL_ID,
          audioConfig: {
            audioEncoding: 'LINEAR16',
            sampleRateHertz: SAMPLE_RATE,
          },
        }),
        signal: abortRef.current.signal,
      })

      if (!res.ok || !res.body) {
        const err = await res.text().catch(() => res.status.toString())
        console.error('[Inworld] HTTP error:', res.status, err)
        return
      }

      // NDJSON stream: each line {"result":{"audioContent":"<base64_WAV>"}}
      // Each audioContent is a complete WAV file — play them in sequence
      const reader = res.body.getReader()
      const decoder = new TextDecoder()
      let buf = ''
      const wavBlobs: Blob[] = []

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        buf += decoder.decode(value, { stream: true })
        const lines = buf.split('\n')
        buf = lines.pop() || ''
        for (const line of lines) {
          const trimmed = line.trim()
          if (!trimmed) continue
          try {
            const msg = JSON.parse(trimmed)
            const b64 = msg?.result?.audioContent
            if (!b64) continue
            // Inworld returns complete WAV files — use directly as Blob
            const raw = atob(b64)
            const bytes = new Uint8Array(raw.length)
            for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
            wavBlobs.push(new Blob([bytes], { type: 'audio/wav' }))
          } catch { /* skip malformed */ }
        }
      }

      if (wavBlobs.length === 0) return

      // Concatenate all WAV blobs into one and queue
      // Actually simpler: merge all PCM data into one WAV
      const allBytes: Uint8Array[] = []
      let totalPcmBytes = 0
      for (const blob of wavBlobs) {
        const ab = await blob.arrayBuffer()
        const bytes = new Uint8Array(ab)
        // Skip 44-byte WAV header for all but first, concat PCM
        allBytes.push(bytes)
        totalPcmBytes += bytes.length - 44
      }

      // Build single WAV: use first chunk's header, concat all PCM
      const first = allBytes[0]
      const merged = new Uint8Array(44 + totalPcmBytes)
      merged.set(first.slice(0, 44)) // WAV header from first chunk
      // Fix data chunk size in header
      const view = new DataView(merged.buffer)
      view.setUint32(4, 36 + totalPcmBytes, true)  // RIFF chunk size
      view.setUint32(40, totalPcmBytes, true)        // data chunk size
      let offset = 44
      for (const chunk of allBytes) {
        const pcm = chunk.slice(44)
        merged.set(pcm, offset)
        offset += pcm.length
      }

      const finalBlob = new Blob([merged], { type: 'audio/wav' })
      queueRef.current.push(finalBlob)
      if (!isPlayingRef.current) playNext()

    } catch (err: any) {
      if (err?.name !== 'AbortError') console.error('[Inworld] fetch error:', err)
    }
  }, [enabled, playNext])

  const stop = useCallback(() => {
    if (abortRef.current) { abortRef.current.abort(); abortRef.current = null }
    queueRef.current = []
    isPlayingRef.current = false
    setIsPlaying(false)
    const audio = getAudio()
    audio.pause()
    audio.src = ''
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current)
      currentUrlRef.current = null
    }
  }, [getAudio])

  return { isConnected: enabled && !!INWORLD_KEY, isPlaying, speak, stop }
}
